// frontend/src/components/chat/MessageComponents.js
// Stable message-rendering components moved outside ChatInterface to prevent re-creation on every render
import React, { useState, useMemo } from 'react';
import { sanitizeInlineHTML, sanitizeRichHTML } from '../../utils/sanitize';
import {
  Box,
  Paper,
  Typography,
  Button,
  Collapse,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Alert
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { MessagePerformanceIndicator } from './TemplateComponents';
import DynamicTemplateResults from './DynamicTemplateResults';

// ResearchSummary component for displaying multi-step research results
export const ResearchSummary = ({ summary }) => {
  const [expanded, setExpanded] = useState(false);

  // Safety check - ensure summary is an array
  if (!summary || !Array.isArray(summary) || summary.length === 0) {
    return null;
  }

  return (
    <Box>
      <Button
        size="small"
        onClick={() => setExpanded(!expanded)}
        sx={{ color: 'inherit', p: 0 }}
      >
        {expanded ? 'Hide' : 'Show'} Research Steps
      </Button>
      <Collapse in={expanded}>
        <Stepper orientation="vertical" sx={{ mt: 1 }}>
          {summary.map((step, index) => (
            <Step key={index} active={true} completed={step.success}>
              <StepLabel
                StepIconComponent={step.success ? CheckCircleIcon : ErrorIcon}
                sx={{ '& .MuiStepLabel-label': { color: 'inherit' } }}
              >
                {step.description}
              </StepLabel>
              <StepContent>
                <Typography variant="caption">
                  {step.success ? `${step.rowCount} rows found` : `Error: ${step.error}`}
                </Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Collapse>
    </Box>
  );
};

// Memoized ReadableTextRenderer to prevent text reprocessing on every keystroke
export const ReadableTextRenderer = React.memo(({ text }) => {
  // Memoize the formatted text to avoid reprocessing
  const formattedLines = useMemo(() => {
    const formatText = (rawText) => {
      if (!rawText || rawText.length < 100) {
        return [rawText]; // Short text doesn't need formatting
      }

      // Break text into readable chunks at sentence boundaries
      return rawText
        .replace(/\. ([A-Z])/g, '.\n\n$1') // Break at ". [Capital letter]"
        .replace(/(\*\*[^\*]+\*\*:)/g, '\n\n$1') // Break before bold labels
        .replace(/(## [^\n]+)/g, '\n\n$1\n') // Add spacing around headers
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0) // Remove empty lines
        .map(line => {
          // Bold formatting
          const formattedLine = line.replace(/\*\*([^\*]+)\*\*/g, '<strong>$1</strong>');
          return formattedLine;
        })
        .filter(line => line.length > 0); // Remove any remaining empty lines
    };

    return formatText(text);
  }, [text]); // Only recalculate when text prop changes

  return (
    <Box sx={{ '& p': { mb: 1.5, lineHeight: 1.7 }, '& strong': { fontWeight: 600, color: '#1976d2' } }}>
      {formattedLines.map((line, index) => (
        <Typography
          key={index}
          variant="body1"
          component="p"
          dangerouslySetInnerHTML={{ __html: sanitizeInlineHTML(line) }}
          sx={{ mb: 1.5 }}
        />
      ))}
    </Box>
  );
});

// QueryResults Section Component
export const QueryResultsSection = React.memo(({ queryResults }) => {
  const [expanded, setExpanded] = useState(false);

  if (!queryResults) return null;

  // Handle different queryResults formats
  let resultsToShow = [];

  if (Array.isArray(queryResults)) {
    // Multi-query template format: [{ id: "revenue_metrics", name: "Revenue Overview", results: [...] }]
    resultsToShow = queryResults.filter(result => result && result.results && result.results.length > 0);
  } else if (queryResults.results) {
    // Single query result format
    resultsToShow = [{ name: 'Query Results', results: queryResults.results }];
  } else {
    // Direct results array
    resultsToShow = [{ name: 'Query Results', results: Array.isArray(queryResults) ? queryResults : [] }];
  }

  if (resultsToShow.length === 0) return null;

  return (
    <Card sx={{ mt: 2, border: '1px solid', borderColor: 'divider' }}>
      <CardActions sx={{ py: 1 }}>
        <Button
          size="small"
          startIcon={<AnalyticsIcon />}
          onClick={() => setExpanded(!expanded)}
          sx={{ fontSize: '0.8rem' }}
        >
          {expanded ? 'Hide' : 'Show'} Raw Data ({resultsToShow.length} dataset{resultsToShow.length > 1 ? 's' : ''})
        </Button>
      </CardActions>
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0 }}>
          {resultsToShow.map((resultSet, index) => (
            <Box key={index} sx={{ mb: index < resultsToShow.length - 1 ? 3 : 0 }}>
              {resultsToShow.length > 1 && (
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  {resultSet.name || `Dataset ${index + 1}`}
                </Typography>
              )}
              <Box sx={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <pre style={{
                  margin: 0,
                  padding: '12px',
                  fontSize: '11px',
                  lineHeight: '1.4',
                  backgroundColor: '#f8f9fa',
                  overflow: 'auto'
                }}>
                  {JSON.stringify(resultSet.results, null, 2)}
                </pre>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Collapse>
    </Card>
  );
});

// Memoized HTMLRenderer to prevent flickering on every keystroke
export const HTMLRenderer = React.memo(({ html }) => {
  const [showHTML, setShowHTML] = useState(true);

  // Memoize the complete HTML to avoid regeneration on every render
  const completeHTML = useMemo(() => {
    if (html.includes('<!DOCTYPE html>')) return html;

    return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        /* Base styles with improved readability */
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          margin: 0;
          padding: 15px;
          line-height: 1.7;
          width: 100%;
          background-color: #fafafa;
          color: #333;
        }

        /* Table improvements */
        table {
          border-collapse: collapse;
          width: auto;
          margin: 25px 0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          border-radius: 6px;
          overflow: hidden;
        }
        th, td {
          border: 1px solid #e0e0e0;
          padding: 14px 16px;
          text-align: left;
        }
        th {
          background-color: #f8f9fa;
          font-weight: 600;
          color: #2c3e50;
          border-bottom: 2px solid #dee2e6;
        }

        /* Typography improvements */
        h1, h2, h3 {
          color: #2c3e50;
          margin-top: 35px;
          margin-bottom: 15px;
          font-weight: 600;
        }
        h1 { font-size: 28px; }
        h2 { font-size: 24px; }
        h3 { font-size: 20px; }

        /* Paragraph spacing */
        p {
          margin: 15px 0;
          line-height: 1.7;
        }

        /* Enhanced metric styling */
        .metric {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 15px 18px;
          border-radius: 8px;
          margin: 15px 0;
          border-left: 4px solid #007bff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        /* MCP Response styling improvements */
        .mcp-response {
          background: white;
          border-radius: 12px;
          padding: 20px;
          margin: 15px 0;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          border: 1px solid #e9ecef;
        }

        .mcp-header {
          border-bottom: 2px solid #f8f9fa;
          padding-bottom: 15px;
          margin-bottom: 20px;
        }

        .mcp-header h3 {
          margin: 0 0 10px 0;
          color: #2c3e50;
          font-size: 22px;
          font-weight: 600;
        }

        .mcp-tools {
          margin-top: 12px;
        }

        .tools-label {
          font-weight: 500;
          color: #6c757d;
          margin-right: 8px;
        }

        .tool-badge {
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
          color: white;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
          margin-right: 8px;
          display: inline-block;
          box-shadow: 0 2px 4px rgba(0,123,255,0.2);
        }

        .response-text {
          font-size: 15px;
          line-height: 1.7;
          margin: 20px 0;
        }

        .response-text p {
          margin: 18px 0;
          text-align: justify;
          line-height: 1.8;
        }

        .response-text p:first-child {
          margin-top: 0;
        }

        .response-text p:last-child {
          margin-bottom: 0;
        }

        .response-text h3 {
          color: #2c3e50;
          font-size: 18px;
          font-weight: 600;
          margin: 25px 0 12px 0;
          border-bottom: 2px solid #007bff;
          padding-bottom: 5px;
        }

        .response-text h4 {
          color: #2c3e50;
          font-size: 16px;
          font-weight: 600;
          margin: 20px 0 10px 0;
          border-left: 4px solid #007bff;
          padding-left: 10px;
          background: rgba(0, 123, 255, 0.05);
          padding: 8px 0 8px 10px;
          border-radius: 0 4px 4px 0;
        }

        .response-text ul {
          margin: 15px 0;
          padding-left: 20px;
        }

        .response-text li {
          margin: 8px 0;
          line-height: 1.6;
        }

        .response-text strong {
          color: #2c3e50;
          font-weight: 600;
          background: rgba(0, 123, 255, 0.1);
          padding: 2px 4px;
          border-radius: 3px;
        }

        .response-text em {
          font-style: italic;
          color: #495057;
        }

        .mcp-meta {
          background: #f8f9fa;
          padding: 12px 16px;
          border-radius: 8px;
          margin-top: 20px;
          border-left: 4px solid #28a745;
        }

        .mcp-meta small {
          color: #6c757d;
          font-size: 13px;
          font-weight: 500;
        }
      </style>
    </head>
    <body>
      ${html}
    </body>
    </html>
  `;
  }, [html]); // Only recalculate when html prop changes

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<VisibilityIcon />}
          onClick={() => setShowHTML(!showHTML)}
          size="small"
          variant="contained"
        >
          {showHTML ? 'Hide' : 'Show'} Enhanced Analysis Report
        </Button>
      </Box>

      <Collapse in={showHTML}>
        <Box
          sx={{
            border: '1px solid #ddd',
            borderRadius: 1,
            overflow: 'visible', // Changed from 'hidden' to allow full content
            width: '100%',
            minHeight: '700px'
          }}
        >
          <iframe
            srcDoc={completeHTML}
            sandbox="allow-same-origin"
            style={{
              width: '100%',
              minHeight: '700px',
              height: 'auto',
              border: 'none',
              display: 'block'
            }}
            title="Analysis Report"
            scrolling="auto"
          />
        </Box>
      </Collapse>
    </Box>
  );
});

// QueryResultsDisplay component
export const QueryResultsDisplay = ({ queryResults }) => {
  const [showQueries, setShowQueries] = useState(false);

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        startIcon={<CodeIcon />}
        onClick={() => setShowQueries(!showQueries)}
        size="small"
        variant="outlined"
      >
        {showQueries ? 'Hide' : 'Show'} Research Details ({(queryResults || []).length} steps)
      </Button>

      <Collapse in={showQueries}>
        <Box sx={{ mt: 2 }}>
          {(queryResults || []).map((result, index) => (
            <Card key={index} sx={{ mb: 2, bgcolor: 'grey.50' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Research Step {result.step}: {result.reasoning}
                </Typography>
                <Typography variant="caption" color="text.secondary" paragraph>
                  {result.rowCount} rows returned
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'white', fontFamily: 'monospace' }}>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {result.sql}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// Main MessageRenderer component - memoized and stable
export const MessageRenderer = React.memo(({ message }) => {
  if (message.sender === 'user') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Paper sx={{
          p: 2,
          maxWidth: '70%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: '18px 18px 4px 18px'
        }}>
          <Typography variant="body1">{message.text || '[Empty message]'}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
            {message.timestamp ? message.timestamp.toLocaleTimeString() : 'Now'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // A refused conversation (DT-1501) is shown as a warning, so it does not read like an ordinary reply
  if (message.severity === 'warning') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
        <Alert severity="warning" sx={{ maxWidth: '80%' }}>{message.text}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, width: '100%' }}>
      <Paper sx={{
        p: 2,
        maxWidth: '80%',
        width: message.type === 'html' ? '100%' : 'auto', // Force full width for HTML
        minWidth: message.type === 'html' ? '100%' : 'auto', // Ensure minimum full width
        bgcolor: 'grey.100',
        borderRadius: '18px 18px 18px 4px'
      }}>
        {/* Research Progress Indicator */}
        {message.researchSteps && (
          <Card sx={{ mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <CardContent sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SearchIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2">
                  Multi-Step Research Completed ({message.researchSteps} steps)
                </Typography>
              </Box>
              {message.researchSummary && (
                <ResearchSummary summary={message.researchSummary} />
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Response */}
        {message.type === 'html' && message.html ? (
          <HTMLRenderer html={message.html} />
        ) : message.type === 'template-result' && (message.html || message.data) ? (
          <>
            {/* Template Result Display */}
            <Typography variant="body1" sx={{ mb: 2, color: 'success.main', fontWeight: 'bold' }}>
              {message.text}
            </Typography>

            {/* Execution Details */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip icon={<CheckCircleIcon />} label="Template" size="small" color="success" />
              {!message.html && message.data && (
                <Chip icon={<AnalyticsIcon />} label={`${message.data.length} rows`} size="small" color="info" />
              )}
              <Chip label={`${message.execution_time}ms`} size="small" color="primary" />
              {message.template_type && (
                <Chip label={message.template_type} size="small" color="default" />
              )}
            </Box>

            {/* Multi-query template HTML or single-query data table */}
            {message.html ? (
              <Box
                sx={{
                  mt: 2,
                  '& .weekly-dashboard, & .products-dashboard, & .funnel-dashboard': {
                    fontFamily: 'inherit'
                  }
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeRichHTML(message.html) }}
              />
            ) : message.data && message.data.length > 0 ? (
              <Box sx={{ mt: 2, overflowX: 'auto' }}>
                <table style={{ width: 'auto', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      {Object.keys(message.data[0]).map(key => (
                        <th key={key} style={{
                          padding: '8px 12px',
                          textAlign: 'left',
                          borderBottom: '2px solid #ddd',
                          fontWeight: 'bold',
                          textTransform: 'capitalize'
                        }}>
                          {key.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {message.data.map((row, index) => (
                      <tr key={index} style={{
                        backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa',
                        borderBottom: '1px solid #eee'
                      }}>
                        {Object.values(row).map((value, i) => (
                          <td key={i} style={{
                            padding: '8px 12px',
                            textAlign: typeof value === 'number' ? 'right' : 'left'
                          }}>
                            {typeof value === 'number' && value % 1 !== 0
                              ? value.toFixed(2)
                              : value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            ) : null}
          </>
        ) : message.type === 'dynamic-template-result' ? (
          <>
            <DynamicTemplateResults
              data={message.data}
              template={message.template}
              parameters={message.parameters_used}
              dynamicMetadata={message.dynamic_metadata}
              executionMetadata={message.execution_metadata}
              html={message.html}
              template_type={message.template_type}
            />
          </>
        ) : message.type === 'template-execution' ? (
          <>
            <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">{message.text}</Typography>
              </Box>
              {message.parameters && Object.keys(message.parameters).length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(message.parameters).map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`${key}: ${value}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Box>
          </>
        ) : (
          <ReadableTextRenderer text={message.text || '[Empty message]'} />
        )}

        {/* Summary Chip */}
        {message.summary && (
          <Chip
            icon={<AnalyticsIcon />}
            label={message.summary}
            variant="outlined"
            size="small"
            sx={{ mt: 1, mb: 1 }}
          />
        )}

        {/* Query Results Toggle */}
        {message.hasQuery && message.queryResults && (
          <QueryResultsSection queryResults={message.queryResults} />
        )}

        {/* Performance Indicators */}
        {message.sender === 'assistant' && message.performance && (
          <MessagePerformanceIndicator performance={message.performance} />
        )}

        {/* Expert & Cache Information */}
        {message.sender === 'assistant' && (message.expert || message.provider || message.cacheType || message.autoRouted) && (
          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {message.expert && (
              <Chip
                label={`${message.expert.name}`}
                size="small"
                sx={{ fontSize: '0.7em', height: '20px' }}
                color="primary"
                variant="outlined"
              />
            )}
            {message.provider && (
              <Chip
                label={message.provider}
                size="small"
                sx={{ fontSize: '0.7em', height: '20px' }}
                color="secondary"
                variant="outlined"
              />
            )}
            {message.autoRouted && (
              <Chip
                label={`Auto: ${message.routingReason || 'Smart selection'}`}
                size="small"
                sx={{ fontSize: '0.7em', height: '20px' }}
                color="primary"
                variant="filled"
                title={`Auto-routing selected ${message.provider} because: ${message.routingReason}`}
              />
            )}
            {message.cacheType && message.cacheType !== 'none' && (
              <Chip
                label={`${message.cacheType} cache`}
                size="small"
                sx={{ fontSize: '0.7em', height: '20px' }}
                color="success"
                variant="filled"
              />
            )}
            {message.responseTime && (
              <Chip
                label={`${message.responseTime}ms`}
                size="small"
                sx={{ fontSize: '0.7em', height: '20px', opacity: 0.8 }}
                variant="outlined"
              />
            )}
          </Box>
        )}

        <Typography variant="caption" sx={{ opacity: 0.6, mt: 1, display: 'block' }}>
          {message.timestamp.toLocaleTimeString()}
        </Typography>
      </Paper>
    </Box>
  );
});

// Display names for debugging
ResearchSummary.displayName = 'ResearchSummary';
ReadableTextRenderer.displayName = 'ReadableTextRenderer';
QueryResultsSection.displayName = 'QueryResultsSection';
HTMLRenderer.displayName = 'HTMLRenderer';
QueryResultsDisplay.displayName = 'QueryResultsDisplay';
MessageRenderer.displayName = 'MessageRenderer';
